/**
 * TeamMemberProfile.jsx
 *
 * A limited profile view available to Project Managers.
 * Shows ONLY:
 *   1. General info header (same as ViewProfile header)
 *   2. Feedback tab (new feedback form + history with violations/attendance tabs)
 *
 * No access to: Attendance Tracking, Requests, Documents, Contract, Evaluation.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell 
} from 'recharts';
import RichTextEditor from '../RichTextEditor';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  MessageSquare,
  UserCheck,
  FileText,
  Send,
  Trash2,
  Edit2,
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  CreditCard,
  User,
  Calendar,
  Loader2,
} from 'lucide-react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { Box, CircularProgress, Typography, Alert, Chip, useTheme } from '@mui/material';
import {
  getTeamMemberById,
  getTeamMemberImage,
  getTeamMemberFeedbacks,
  addTeamMemberFeedback,
  deleteTeamMemberFeedback,
  updateTeamMemberFeedback,
} from '../../services/projectManagerAPIs';
import { formatPositionsDisplay } from '../../services/positionAPIs';

// ─── Constants ──────────────────────────────────────────────────────────────────
const VIOLATION_TYPES = [
  'Violation of Rules',
  'Behavior',
  'Work Performance',
  'Break Compliance',
  'Unauthorized Absence',
  'Early Departure',
  'Insubordination',
  'Harassment or Discrimination',
  'Use of Mobile Phone During Work',
  'Dress Code Violation',
  'Unprofessional Communication',
  'Disruption of Workplace',
  'Confidentiality Breach',
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  } catch {
    return 'Invalid Date';
  }
};

// ─── Component ──────────────────────────────────────────────────────────────────
const TeamMemberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const dm = {
    bg:            isDark ? theme.palette.background.default : '#F3F4F6',
    card:          isDark ? theme.palette.background.paper   : '#FFFFFF',
    border:        isDark ? theme.palette.divider            : '#E5E7EB',
    textPrimary:   theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    input:         isDark ? theme.palette.background.default : '#FFFFFF',
    inputBorder:   isDark ? theme.palette.divider            : '#D1D5DB',
    noteRow:       isDark ? theme.palette.action.hover       : '#FAFAFA',
    reactionBg:    isDark ? theme.palette.action.selected    : '#F5F3FF',
    reactionBorder:isDark ? '#4c1d95'                        : '#DDD6FE',
    agreementBg:   isDark ? theme.palette.action.selected    : '#FFFBEB',
    agreementBorder:isDark ? '#78350f'                       : '#FDE68A',
  };

  // ── State ──
  const [employee, setEmployee] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    date: new Date().toISOString().split('T')[0],
    violationType: VIOLATION_TYPES[0],
    description: '',
    managerName: '',
    employeeReaction: '',
    employeeAgreement: '',
  });

  // Edit state
  const [editingNote, setEditingNote] = useState(null);
  const [editForm, setEditForm] = useState({});

  // History filters
  const [feedbackTab, setFeedbackTab] = useState('violations');
  const [feedbackYear, setFeedbackYear] = useState(new Date().getFullYear());
  const [feedbackMonth, setFeedbackMonth] = useState('All');

  // ── Violation type counts ──
  const violationTypeCounts = useMemo(() => {
    const counts = {};
    VIOLATION_TYPES.forEach((t) => { counts[t] = 0; });
    notes.forEach((n) => {
      if (n.violationType && Object.prototype.hasOwnProperty.call(counts, n.violationType)) {
        counts[n.violationType]++;
      }
    });
    return counts;
  }, [notes]);

  // ── Load employee & notes ──
  useEffect(() => {
    if (!id) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [emp, fetchedNotes] = await Promise.all([
          getTeamMemberById(id),
          getTeamMemberFeedbacks(id),
        ]);
        if (!active) return;
        setEmployee(emp);
        setNotes(fetchedNotes || []);
        if (emp?.image) {
          const url = await getTeamMemberImage(emp.employeeId || emp.id);
          if (active) setProfileImageUrl(url);
        }
      } catch (err) {
        if (active) setError('Failed to load team member profile.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id]);

  const refreshNotes = async () => {
    try {
      const fresh = await getTeamMemberFeedbacks(id);
      setNotes(fresh || []);
    } catch {
      // ignore
    }
  };

  // ── Feedback form handlers ──
  const handleFeedbackChange = (e) => {
    const { name, value } = e.target;
    setFeedbackForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackForm.description.trim() || !feedbackForm.managerName.trim()) {
      setError('Please fill in the Description and Manager Name.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    try {
      await addTeamMemberFeedback(id, {
        incidentDate: feedbackForm.date,
        violationType: feedbackForm.violationType,
        managerReview: feedbackForm.managerName,
        description: feedbackForm.description,
        employeeReaction: feedbackForm.employeeReaction,
        employeeAgreement: feedbackForm.employeeAgreement,
      });
      setFeedbackForm({
        date: new Date().toISOString().split('T')[0],
        violationType: VIOLATION_TYPES[0],
        description: '',
        managerName: '',
        employeeReaction: '',
        employeeAgreement: '',
      });
      await refreshNotes();
      setSuccess('Feedback submitted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to submit feedback.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this feedback record?')) return;
    try {
      await deleteTeamMemberFeedback(noteId);
      await refreshNotes();
      setSuccess('Feedback deleted.');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to delete feedback.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // ── Edit handlers ──
  const handleEditClick = (note) => {
    setEditingNote(note);
    setEditForm({
      date: note.incidentDate || note.date || new Date().toISOString().split('T')[0],
      violationType: note.violationType || VIOLATION_TYPES[0],
      description: note.description || '',
      managerName: note.managerReview || '',
      employeeReaction: note.employeeReaction || '',
      employeeAgreement: note.employeeAgreement || '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    if (!editForm.description.trim() || !editForm.managerName.trim()) {
      setError('Please fill in the Description and Manager Name.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    try {
      await updateTeamMemberFeedback(editingNote.id, {
        incidentDate: editForm.date,
        violationType: editForm.violationType,
        managerReview: editForm.managerName,
        description: editForm.description,
        employeeReaction: editForm.employeeReaction,
        employeeAgreement: editForm.employeeAgreement,
      });
      setEditingNote(null);
      setEditForm({});
      await refreshNotes();
      setSuccess('Feedback updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to update feedback.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // ── Filtered notes ──
  const filteredNotes = useMemo(() => {
    return notes.filter((note) => {
      const noteDate = note.incidentDate || note.createdDate || note.date;
      if (!noteDate) return true;
      const d = new Date(noteDate);
      if (d.getFullYear() !== feedbackYear) return false;
      if (feedbackMonth !== 'All' && d.getMonth() !== Number(feedbackMonth)) return false;
      return true;
    });
  }, [notes, feedbackYear, feedbackMonth]);

  // ── Loading / error states ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Team member not found.</Alert>
      </Box>
    );
  }

  const fullName = `${employee.firstName?.trim() || ''} ${employee.lastName?.trim() || ''}`.trim();
  const positions = formatPositionsDisplay(employee);

  // ── Render ──
  return (
    <div style={{ minHeight: '100vh', backgroundColor: dm.bg, padding: '2rem' }}>
      {/* ── Back button ── */}
      <button
        onClick={() => navigate('/my-team')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#3B82F6',
          fontWeight: '600',
          marginBottom: '1.5rem',
          padding: 0,
          fontSize: '0.9rem',
        }}
      >
        <ChevronLeft size={20} />
        Back to My Team
      </button>

      {/* ── Header card ── */}
      <div
        style={{
          backgroundColor: dm.card,
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                overflow: 'hidden',
                background: profileImageUrl ? 'transparent' : '#3B82F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                fontWeight: 700,
                color: 'white',
                border: '3px solid #3B82F6',
              }}
            >
              {profileImageUrl ? (
                <img src={profileImageUrl} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                `${(employee.firstName || '?').charAt(0)}${(employee.lastName || '').charAt(0)}`.toUpperCase()
              )}
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                border: '2px solid white',
              }}
            />
          </div>

          {/* Name & position */}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: dm.textPrimary, margin: 0 }}>
              {fullName}
            </h1>
            <p style={{ fontSize: '1rem', color: dm.textSecondary, marginTop: '0.25rem', marginBottom: '1rem' }}>
              {positions}
            </p>

            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {[
                { icon: <Mail size={16} color="#3B82F6" />, label: 'Email', value: employee.email },
                { icon: <Phone size={16} color="#3B82F6" />, label: 'Phone', value: employee.phone },
                { icon: <MapPin size={16} color="#3B82F6" />, label: 'Office', value: employee.office?.name || employee.officeName },
                { icon: <Briefcase size={16} color="#3B82F6" />, label: 'Department', value: employee.department?.name || employee.departmentName },
                { icon: <Briefcase size={16} color="#3B82F6" />, label: 'Projects', value: (employee.projects || []).map(p => typeof p === 'string' ? p : (p.projectName || p.name)).join(', ') || employee.projectName },
                { icon: <User size={16} color="#3B82F6" />, label: 'Gender', value: employee.gender },
                { icon: <Calendar size={16} color="#3B82F6" />, label: 'Join Date', value: formatDate(employee.dateOfJoining) },
                { icon: <Calendar size={16} color="#3B82F6" />, label: 'Date of Birth', value: formatDate(employee.dob) },
                { icon: <Award size={16} color="#3B82F6" />, label: 'Diploma', value: employee.diploma },
                { icon: <CreditCard size={16} color="#3B82F6" />, label: 'CNSS', value: employee.cnss },
                { icon: <CreditCard size={16} color="#3B82F6" />, label: 'CIN', value: employee.cin },
              ].map(({ icon, label, value }) =>
                value && value !== 'N/A' ? (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {icon}
                    <div>
                      <p style={{ fontSize: '0.7rem', color: dm.textSecondary, margin: 0 }}>{label}</p>
                      <p style={{ fontSize: '0.85rem', fontWeight: 500, color: dm.textPrimary, margin: 0 }}>{value}</p>
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error && (
        <div style={{ backgroundColor: isDark ? '#450a0a' : '#FEF2F2', border: `1px solid ${isDark ? '#991b1b' : '#FCA5A5'}`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: isDark ? '#fca5a5' : '#B91C1C', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}
      {success && (
        <div style={{ backgroundColor: isDark ? '#052e16' : '#F0FDF4', border: `1px solid ${isDark ? '#166534' : '#86EFAC'}`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: isDark ? '#86efac' : '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ✓ {success}
        </div>
      )}

      {/* ── Feedback Form ── */}
      <div
        style={{
          backgroundColor: dm.card,
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}
      >
        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <FileText size={22} color="#3B82F6" />
          New Feedback Record
        </h2>

        {/* Row 1: Date, Violation Type, Manager */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Date */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
              Incident Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={new Date(feedbackForm.date)}
                onChange={(v) => {
                  if (v) setFeedbackForm((p) => ({ ...p, date: format(v, 'yyyy-MM-dd') }));
                }}
                format="dd-MM-yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.5rem',
                        backgroundColor: dm.input,
                        color: dm.textPrimary,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: dm.inputBorder,
                      },
                      '& .MuiSvgIcon-root': {
                        color: dm.textSecondary,
                      },
                      '& .MuiInputBase-input': {
                        color: dm.textPrimary,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Violation Type */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
              Type of Violation
            </label>
            <div style={{ position: 'relative' }}>
              <AlertTriangle size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#EF4444' }} />
              <select
                name="violationType"
                value={feedbackForm.violationType}
                onChange={handleFeedbackChange}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem 0.6rem 2.2rem',
                  border: `1px solid ${dm.inputBorder}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  backgroundColor: dm.input,
                  color: dm.textPrimary,
                  appearance: 'none',
                }}
              >
                {VIOLATION_TYPES.map((t) => {
                  const cnt = violationTypeCounts[t] || 0;
                  return (
                    <option key={t} value={t}>
                      {t} {cnt > 0 ? `(${cnt})` : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Manager */}
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
              Manager Review by
            </label>
            <div style={{ position: 'relative' }}>
              <UserCheck size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#3B82F6' }} />
              <input
                type="text"
                name="managerName"
                placeholder="Enter manager name"
                value={feedbackForm.managerName}
                onChange={handleFeedbackChange}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem 0.6rem 2.2rem',
                  border: `1px solid ${dm.inputBorder}`,
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  backgroundColor: dm.input,
                  color: dm.textPrimary,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
            Description of Incident
          </label>
          <RichTextEditor
            value={feedbackForm.description || ''}
            onChange={(val) => handleFeedbackChange({ target: { name: 'description', value: val } })}
          />
        </div>

        {/* Reaction & Agreement */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
              Employee Reaction
            </label>
            <div style={{ position: 'relative' }}>
              <RichTextEditor
                value={feedbackForm.employeeReaction || ''}
                onChange={(val) => handleFeedbackChange({ target: { name: 'employeeReaction', value: val } })}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: dm.textPrimary, marginBottom: '0.5rem' }}>
              Employee Agreement / Action
            </label>
            <div style={{ position: 'relative' }}>
              <RichTextEditor
                value={feedbackForm.employeeAgreement || ''}
                onChange={(val) => handleFeedbackChange({ target: { name: 'employeeAgreement', value: val } })}
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSubmitFeedback}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '600',
              fontSize: '0.9rem',
              boxShadow: '0 2px 4px rgba(59,130,246,0.3)',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2563EB')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3B82F6')}
          >
            <Send size={16} />
            Submit Feedback
          </button>
        </div>
      </div>

      {/* ── Feedback History ── */}
      <div style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
        {/* Header + filters */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: dm.textPrimary, margin: 0 }}>History</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              value={feedbackYear}
              onChange={(e) => setFeedbackYear(Number(e.target.value))}
              style={{ padding: '0.5rem', borderRadius: '0.375rem', border: `1px solid ${dm.inputBorder}`, fontSize: '0.875rem', backgroundColor: dm.input, color: dm.textPrimary }}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={feedbackMonth}
              onChange={(e) => setFeedbackMonth(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '0.375rem', border: `1px solid ${dm.inputBorder}`, fontSize: '0.875rem', backgroundColor: dm.input, color: dm.textPrimary }}
            >
              <option value="All">All Months</option>
              {MONTHS.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: '1rem', borderBottom: `1px solid ${dm.border}`, marginBottom: '1.5rem' }}>
          {['violations', 'attendance'].map((tab) => (
            <button
              key={tab}
              onClick={() => setFeedbackTab(tab)}
              style={{
                padding: '0.75rem 1rem',
                borderBottom: feedbackTab === tab ? '2px solid #3B82F6' : '2px solid transparent',
                color: feedbackTab === tab ? '#3B82F6' : dm.textSecondary,
                fontWeight: '600',
                background: 'none',
                border: 'none',
                borderBottomWidth: '2px',
                borderBottomStyle: 'solid',
                cursor: 'pointer',
                fontSize: '0.9rem',
                textTransform: 'capitalize',
              }}
            >
              {tab === 'violations' ? 'Violations' : 'Attendance Records'}
            </button>
          ))}
        </div>

        {/* Violations list */}
        {feedbackTab === 'violations' && (
          <div>
            {filteredNotes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: dm.textSecondary }}>
                <FileText size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
                <p>No feedback records for this period.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredNotes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      border: `1px solid ${editingNote?.id === note.id ? '#3B82F6' : dm.border}`,
                      borderRadius: '0.75rem',
                      padding: '1.25rem',
                      backgroundColor: dm.noteRow,
                      transition: 'border-color 0.2s',
                    }}
                  >
                    {editingNote?.id === note.id ? (
                      // ── Edit mode ──
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Date</label>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={editForm.date ? new Date(editForm.date) : null}
                                onChange={(val) => {
                                  if (val) setEditForm((prev) => ({ ...prev, date: format(val, 'yyyy-MM-dd') }));
                                }}
                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { backgroundColor: dm.input } } }}
                              />
                            </LocalizationProvider>
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Violation Type</label>
                            <select
                              name="violationType"
                              value={editForm.violationType}
                              onChange={handleEditFormChange}
                              style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.inputBorder}`, borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: dm.input, color: dm.textPrimary }}
                            >
                              {VIOLATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Manager Name</label>
                          <input
                            name="managerName"
                            value={editForm.managerName}
                            onChange={handleEditFormChange}
                            style={{ width: '100%', padding: '0.5rem', border: `1px solid ${dm.inputBorder}`, borderRadius: '0.375rem', fontSize: '0.875rem', backgroundColor: dm.input, color: dm.textPrimary, boxSizing: 'border-box' }}
                          />
                        </div>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: dm.textSecondary, display: 'block', marginBottom: '0.25rem' }}>Description</label>
                          <RichTextEditor
                            value={editForm.description || ''}
                            onChange={(val) => handleEditFormChange({ target: { name: 'description', value: val } })}
                          />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8B5CF6', display: 'block', marginBottom: '0.25rem' }}>Employee Reaction</label>
                            <RichTextEditor
                              value={editForm.employeeReaction || ''}
                              onChange={(val) => handleEditFormChange({ target: { name: 'employeeReaction', value: val } })}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#F59E0B', display: 'block', marginBottom: '0.25rem' }}>Agreement / Action</label>
                            <RichTextEditor
                              value={editForm.employeeAgreement || ''}
                              onChange={(val) => handleEditFormChange({ target: { name: 'employeeAgreement', value: val } })}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                          <button
                            onClick={() => { setEditingNote(null); setEditForm({}); }}
                            style={{ padding: '0.5rem 1.25rem', backgroundColor: 'transparent', color: dm.textSecondary, border: `1px solid ${dm.inputBorder}`, borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            style={{ padding: '0.5rem 1.25rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      // ── Read-only mode ──
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span
                              style={{
                                padding: '0.2rem 0.75rem',
                                borderRadius: '9999px',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                backgroundColor: isDark ? '#451a03' : '#FEF3C7',
                                color: isDark ? '#fcd34d' : '#92400E',
                                border: `1px solid ${isDark ? '#78350f' : '#FDE68A'}`,
                              }}
                            >
                              {note.violationType || 'General'}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}>
                              {formatDate(note.incidentDate || note.createdDate)}
                            </span>
                            {note.managerReview && (
                              <span style={{ fontSize: '0.8rem', color: '#3B82F6' }}>
                                by {note.managerReview}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <button
                              onClick={() => handleEditClick(note)}
                              title="Edit feedback"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', padding: '0.25rem' }}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              title="Delete feedback"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '0.25rem' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        {note.description && (
                          <div style={{ fontSize: '0.875rem', color: dm.textPrimary, marginBottom: '0.5rem' }} dangerouslySetInnerHTML={{ __html: note.description }} />
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.5rem' }}>
                          {note.employeeReaction && (
                            <div style={{ backgroundColor: dm.reactionBg, borderRadius: '0.5rem', padding: '0.75rem', border: `1px solid ${dm.reactionBorder}` }}>
                              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#8B5CF6', marginBottom: '0.25rem' }}>EMPLOYEE REACTION</p>
                              <p style={{ fontSize: '0.8rem', color: dm.textPrimary, margin: 0 }}>{note.employeeReaction}</p>
                            </div>
                          )}
                          {note.employeeAgreement && (
                            <div style={{ backgroundColor: dm.agreementBg, borderRadius: '0.5rem', padding: '0.75rem', border: `1px solid ${dm.agreementBorder}` }}>
                              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: '#F59E0B', marginBottom: '0.25rem' }}>AGREEMENT / ACTION</p>
                              <p style={{ fontSize: '0.8rem', color: dm.textPrimary, margin: 0 }}>{note.employeeAgreement}</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Attendance records tab — show a message for now */}
        {feedbackTab === 'attendance' && (
          <div style={{ textAlign: 'center', padding: '3rem', color: dm.textSecondary }}>
            <p>Attendance records are managed by HR.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberProfile;
